/* Hallmark · pre-emit critique: P5 H5 E5 S5 R4 V5 · macrostructure: Bento Grid · F1 knobs: tiles=6, spans=irregular, accent=corner-only
 * tone: operational-editorial · anchor hue: heritage-green · theme: Operational Architect · nav: N3 side-rail · enrichment: none
 * contrast: pass (40–41) · slop: pass (42–45) · responsive: pass (34, 49–57) · icons: pass (30)
 */
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Plus,
  ClipboardCheck,
  CheckSquare,
  Cloud,
  CheckCircle2,
  LayoutDashboard,
  ClipboardList,
  Megaphone,
  ChevronRight,
  AlertCircle,
  BookOpen,
  Sliders,
  Loader2,
  ArrowUpRight,
  BarChart3,
  Clock3,
  CalendarDays,
  RefreshCw,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from '@lms/ui';
import { resolveMediaUrl } from '@lms/shared/utils';
import { useProgramsOverview } from '../hooks/usePrograms';
import { useSystemHealth, usePendingTasks } from '../hooks/useSystem';
import { useActiveAnnouncements } from '../hooks/useAnnouncements';
import { AnnouncementDetailModal } from '../components/AnnouncementDetailModal';
import { useAuthStore } from '../store/auth';
import { dashboardCopy, copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import { apiBaseUrl, apiClient } from '../lib/api-client';
import type { Announcement } from '@lms/shared/types';

export function Dashboard() {
  const user = useAuthStore(state => state.user);
  const { language, t } = useLanguage();
  const [today, setToday] = useState(() => new Date());

  useEffect(() => {
    const refreshToday = window.setInterval(() => setToday(new Date()), 60 * 1000);
    return () => window.clearInterval(refreshToday);
  }, []);

  const formattedToday = useMemo(
    () =>
      new Intl.DateTimeFormat(language === 'ar' ? 'ar-EG' : 'id-ID', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(today),
    [language, today]
  );

  const isTeacher = Boolean(
    user?.roles?.some(r => {
      if (!r) return false;
      const name = typeof r === 'string' ? r : (r as { role?: { name?: string } })?.role?.name;
      return name?.toUpperCase() === 'TEACHER' || name?.toUpperCase() === 'PENGAJAR';
    })
  );
  const role = isTeacher ? 'pengajar' : 'admin';

  const dCopy = dashboardCopy[role];
  const {
    data: overview,
    isLoading: isOverviewLoading,
    isFetching: isOverviewRefreshing,
    error,
    refetch: refetchOverview,
  } = useProgramsOverview();
  const { data: systemHealth } = useSystemHealth();
  const { data: mailHealth } = useQuery({
    queryKey: ['mail', 'status'],
    queryFn: () => apiClient.mail.getStatus(),
    refetchInterval: 30000,
  });
  const { data: pendingTasks, isLoading: isTasksLoading } = usePendingTasks();
  const { data: activeAnnouncements } = useActiveAnnouncements({
    targetAudience: isTeacher ? 'TEACHER_ONLY' : 'ADMIN_ONLY',
  });
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

  if (isOverviewLoading) {
    return (
      <div className="space-y-8 animate-fadeIn pb-12">
        {/* Dynamic Loading Header Banner */}
        <div className="bg-surface-container-low/70 backdrop-blur-xl rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-level1">
          <div className="flex items-center gap-4">
            <div className="relative flex items-center justify-center">
              <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              </div>
            </div>
            <div>
              <h3 className="text-base font-headline font-bold text-on-surface flex items-center gap-2 m-0">
                <Bilingual {...copy.dashboard.loadingDashboardTitle} />
              </h3>
              <p className="text-xs text-on-surface-variant font-body mt-1 mb-0 opacity-70">
                <Bilingual {...copy.dashboard.loadingDashboardDesc} />
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-surface-container text-xs font-label font-bold text-primary shadow-level1 shrink-0">
            <span className="w-2 h-2 rounded-full bg-primary animate-ping" />
            <Bilingual {...copy.dashboard.loadingRealtimeSync} />
          </div>
        </div>

        {/* Top Header Placeholder */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-2">
          <div className="space-y-3">
            <div className="h-4 w-28 rounded-md animate-shimmer" />
            <div className="h-9 w-64 rounded-xl animate-shimmer" />
            <div className="h-5 w-80 max-w-full rounded-md animate-shimmer opacity-80" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-28 rounded-xl animate-shimmer shadow-level1" />
            <div className="h-10 w-32 rounded-xl animate-shimmer shadow-level1" />
          </div>
        </div>

        {/* 4 Stats Cards Shimmer */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="h-36 rounded-2xl bg-surface-container-lowest p-6 shadow-level1 flex flex-col justify-between overflow-hidden relative"
            >
              <div className="flex items-center justify-between">
                <div className="h-4 w-24 rounded-md animate-shimmer" />
                <div className="w-10 h-10 rounded-xl animate-shimmer shrink-0" />
              </div>
              <div className="space-y-2">
                <div className="h-8 w-20 rounded-lg animate-shimmer" />
                <div className="h-3 w-32 rounded-md animate-shimmer opacity-75" />
              </div>
            </div>
          ))}
        </div>

        {/* Charts & Tasks Grid Shimmer */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-2">
          <div className="lg:col-span-7 h-[420px] rounded-2xl bg-surface-container-lowest p-6 shadow-level1 overflow-hidden flex flex-col justify-between">
            <div className="flex justify-between items-center pb-4">
              <div className="h-5 w-48 rounded-md animate-shimmer" />
              <div className="h-4 w-24 rounded-md animate-shimmer" />
            </div>
            <div className="flex-1 rounded-xl animate-shimmer my-3" />
            <div className="h-4 w-40 rounded-md animate-shimmer opacity-80" />
          </div>

          <div className="lg:col-span-5 h-[420px] rounded-2xl bg-surface-container-lowest p-6 shadow-level1 overflow-hidden flex flex-col justify-between">
            <div className="flex justify-between items-center pb-4">
              <div className="h-5 w-40 rounded-md animate-shimmer" />
              <div className="h-4 w-20 rounded-md animate-shimmer" />
            </div>
            <div className="space-y-4 flex-1 my-3">
              {[1, 2, 3, 4].map(j => (
                <div key={j} className="h-14 rounded-xl animate-shimmer shadow-level1" />
              ))}
            </div>
            <div className="h-4 w-32 rounded-md animate-shimmer opacity-80" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="text-center py-12">
        <p className="text-error font-body">
          <Bilingual {...copy.dashboard.failedLoad} />
        </p>
      </div>
    );
  }

  const stats = overview.stats || {
    totalParticipants: 0,
    activeParticipants: 0,
    activeMaterials: 0,
    completionRate: 0,
    completedFinalEvaluations: 0,
    incompleteParticipants: 0,
    totalRegistrations: 0,
    pendingRegistrations: 0,
    approvedRegistrations: 0,
    pendingToday: 0,
    activeLast7Days: 0,
    incompleteLast7Days: 0,
  };

  const chartData = overview.chartData || [];

  // System status fallback
  const systemStatus =
    systemHealth?.status === 'ok'
      ? language === 'ar'
        ? 'جميع الأنظمة تعمل بشكل طبيعي'
        : 'Semua sistem berfungsi normal'
      : language === 'ar'
        ? 'مطلوب فحص حالة النظام'
        : 'Pemeriksaan status sistem diperlukan';

  const completionRate = Math.min(Math.max(stats.completionRate, 0), 100);
  const pendingCount = pendingTasks?.reduce((total, task) => total + task.count, 0) ?? 0;
  const quickActions = [
    {
      to: '/programs/create',
      label: copy.dashboard.newProgram,
      icon: Plus,
      tone: 'bg-primary text-on-primary hover:bg-primary-container',
    },
    {
      to: '/evaluations/create',
      label: copy.dashboard.createEvaluation,
      icon: ClipboardCheck,
      tone: 'bg-surface-container-high text-on-surface hover:bg-surface-container-highest',
    },
    {
      to: '/registrations',
      label: copy.dashboard.approveRegistration,
      icon: CheckSquare,
      tone: 'bg-secondary text-on-secondary hover:opacity-90',
      badge: stats.pendingRegistrations,
    },
    {
      to: '/reports',
      label: copy.dashboard.reportingCenter,
      icon: BarChart3,
      tone: 'bg-tertiary-container text-on-tertiary-container hover:opacity-90',
    },
  ];

  return (
    <div className="space-y-8 pb-12 animate-fadeIn">
      <header className="grid gap-6 rounded-3xl bg-surface-container-low p-6 sm:p-8 xl:grid-cols-[minmax(0,1.5fr)_minmax(19rem,0.75fr)] xl:items-end">
        <div className="min-w-0">
          <div className="flex flex-col gap-2">
            <Bilingual
              {...dCopy.salam}
              forceBilingual
              size="large"
              className="m-0 text-lg font-bold text-primary sm:text-2xl"
            />
            <p className="m-0 inline-flex items-center gap-2 text-xs font-label font-bold text-on-surface-variant sm:text-sm">
              <CalendarDays size={16} className="shrink-0 text-primary" />
              <span>{t(copy.dashboard.today)}</span>
              <span aria-hidden="true" className="text-on-surface-variant/50">
                ·
              </span>
              <time dateTime={today.toISOString().slice(0, 10)}>{formattedToday}</time>
            </p>
          </div>
          <h1 className="mt-5 max-w-3xl break-words wrap-anywhere font-headline text-3xl font-extrabold tracking-tight text-on-surface sm:text-4xl">
            <Bilingual {...dCopy.ahlan} />
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-on-surface-variant sm:text-base">
            <Bilingual {...dCopy.tagline} stack size="default" />
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-surface-container-lowest px-3 py-2 text-xs font-label font-bold text-on-surface">
              <ShieldCheck size={15} className="text-primary" />
              {systemStatus}
            </span>
            <button
              type="button"
              onClick={() => void refetchOverview()}
              disabled={isOverviewRefreshing}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-surface-container-high px-3 text-xs font-label font-bold text-on-surface transition-colors hover:bg-surface-container-highest focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-40 active:scale-95"
            >
              <RefreshCw size={14} className={isOverviewRefreshing ? 'animate-spin' : ''} />
              <Bilingual
                {...(isOverviewRefreshing
                  ? copy.dashboard.overviewRefreshing
                  : copy.dashboard.refreshOverview)}
              />
            </button>
          </div>
        </div>

        <section className="rounded-2xl bg-primary p-5 text-on-primary shadow-level1">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.15em] text-on-primary/70">
                <Bilingual {...copy.dashboard.operationalFocus} />
              </p>
              <h2 className="mt-3 font-headline text-xl font-extrabold leading-snug">
                {isTasksLoading ? (
                  <Bilingual {...copy.common.loading} />
                ) : pendingCount > 0 ? (
                  <Bilingual {...copy.dashboard.needsAction} />
                ) : (
                  <Bilingual {...copy.dashboard.allCaughtUp} />
                )}
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-on-primary/80">
                {pendingCount > 0
                  ? `${pendingCount} ${t(copy.dashboard.needsAction).toLowerCase()}.`
                  : t(copy.dashboard.noPendingPriority)}
              </p>
            </div>
            <Clock3 size={22} className="shrink-0 text-secondary" />
          </div>
          <Link
            to={pendingCount > 0 ? '/registrations' : '/reports'}
            className="mt-5 inline-flex h-10 items-center gap-2 rounded-lg bg-surface-container-lowest px-4 text-xs font-label font-bold text-primary transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-on-primary active:scale-95"
          >
            <Bilingual
              {...(pendingCount > 0
                ? copy.dashboard.reviewPriority
                : copy.dashboard.reportingCenter)}
            />
            <ArrowUpRight size={15} />
          </Link>
        </section>
      </header>

      <section
        aria-labelledby="quick-actions-title"
        className="rounded-2xl bg-surface-container-low p-4 sm:p-5"
      >
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2
              id="quick-actions-title"
              className="m-0 font-headline text-lg font-bold text-on-surface"
            >
              <Bilingual {...copy.dashboard.quickActions} />
            </h2>
            <p className="mt-1 text-xs text-on-surface-variant">
              <Bilingual {...copy.dashboard.operationalFocusDesc} />
            </p>
          </div>
          <Link
            to="/announcements"
            className="mt-2 inline-flex items-center gap-2 text-xs font-label font-bold text-primary hover:text-primary-container sm:mt-0"
          >
            <Megaphone size={14} />
            <Bilingual {...copy.dashboard.announcementHub} />
            <ChevronRight size={14} />
          </Link>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {quickActions.map(action => {
            const Icon = action.icon;
            return (
              <Link
                key={action.to}
                to={action.to}
                className={`group flex min-w-0 items-center justify-between gap-3 rounded-xl px-4 py-3 text-xs font-label font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95 ${action.tone}`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Icon size={16} />
                  <Bilingual {...action.label} />
                </span>
                {action.badge && action.badge > 0 ? (
                  <span className="grid size-5 shrink-0 place-items-center rounded-full bg-error text-[10px] text-on-error">
                    {action.badge}
                  </span>
                ) : (
                  <ArrowUpRight
                    size={15}
                    className="shrink-0 opacity-70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                )}
              </Link>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="snapshot-title">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
              <Bilingual {...copy.dashboard.operationalSnapshot} />
            </p>
            <h2
              id="snapshot-title"
              className="mt-1 font-headline text-2xl font-extrabold tracking-tight text-on-surface"
            >
              <Bilingual {...copy.dashboard.operationalKPIs} />
            </h2>
          </div>
          <Link
            to="/participants"
            className="inline-flex items-center gap-2 text-xs font-label font-bold text-primary hover:text-primary-container"
          >
            <UsersRound size={15} />
            <Bilingual {...copy.dashboard.participantDirectory} />
            <ChevronRight size={14} />
          </Link>
        </div>
        <div className="grid gap-3 lg:grid-cols-12">
          <article className="min-w-0 rounded-2xl bg-primary p-6 text-on-primary lg:col-span-5">
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-on-primary/70">
              <Bilingual {...copy.dashboard.totalParticipants} />
            </p>
            <p className="mt-4 font-headline text-4xl font-extrabold tracking-tight sm:text-5xl">
              {stats.totalParticipants}
            </p>
            <div className="mt-5 flex items-center gap-3 text-xs text-on-primary/80">
              <UsersRound size={16} />
              <span>
                <Bilingual {...copy.dashboard.activeParticipants} />:{' '}
                <strong className="text-on-primary">{stats.activeParticipants}</strong>
              </span>
            </div>
          </article>
          <article className="rounded-2xl bg-surface-container-lowest p-5 shadow-level1 lg:col-span-3">
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
              <Bilingual {...copy.dashboard.materialsActive} />
            </p>
            <p className="mt-4 font-headline text-3xl font-extrabold text-on-surface">
              {stats.activeMaterials}
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs text-primary">
              <BookOpen size={15} />
              <span>
                <Bilingual {...copy.common.material} />
              </span>
            </div>
          </article>
          <article className="rounded-2xl bg-secondary-container p-5 text-on-secondary-container lg:col-span-4">
            <div className="flex items-start justify-between gap-3">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em]">
                <Bilingual {...copy.dashboard.pendingRegistrations} />
              </p>
              <CheckSquare size={18} />
            </div>
            <p className="mt-4 font-headline text-3xl font-extrabold">
              {stats.pendingRegistrations}
            </p>
            <Link
              to="/registrations"
              className="mt-5 inline-flex items-center gap-1 text-xs font-label font-bold underline decoration-on-secondary-container/30 underline-offset-4 hover:decoration-on-secondary-container"
            >
              <Bilingual {...copy.dashboard.reviewPriority} />
              <ChevronRight size={14} />
            </Link>
          </article>
          <article className="rounded-2xl bg-surface-container-high p-5 lg:col-span-7">
            <div className="flex items-center justify-between gap-4">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                <Bilingual {...copy.dashboard.completionRate} />
              </p>
              <strong className="font-headline text-2xl text-primary">{completionRate}%</strong>
            </div>
            <div className="mt-5 h-2 overflow-hidden rounded-full bg-surface-container-lowest">
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-300"
                style={{ width: `${completionRate}%` }}
              />
            </div>
            <p className="mt-3 text-xs text-on-surface-variant">
              <Bilingual {...copy.dashboard.finalEvaluationsDone} />:{' '}
              <strong className="text-on-surface">{stats.completedFinalEvaluations}</strong>
            </p>
          </article>
          <article className="rounded-2xl bg-error-container p-5 text-on-error-container lg:col-span-5">
            <div className="flex items-start justify-between gap-3">
              <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em]">
                <Bilingual {...copy.dashboard.inProgress} />
              </p>
              <AlertCircle size={18} />
            </div>
            <p className="mt-4 font-headline text-3xl font-extrabold">
              {stats.incompleteParticipants}
            </p>
            <p className="mt-5 text-xs">
              <Bilingual {...copy.dashboard.activeParticipants} />{' '}
              <span className="font-bold">{stats.activeLast7Days}</span>
            </p>
          </article>
        </div>
      </section>

      <div className="grid gap-8 xl:grid-cols-[minmax(0,1.25fr)_minmax(20rem,0.75fr)]">
        <div className="space-y-8">
          <section
            aria-labelledby="tasks-title"
            className="rounded-3xl bg-surface-container-low p-5 sm:p-6"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-xl bg-primary text-on-primary">
                  <CheckCircle2 size={19} />
                </div>
                <div>
                  <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-primary">
                    <Bilingual {...copy.dashboard.operationalFocus} />
                  </p>
                  <h2
                    id="tasks-title"
                    className="mt-1 font-headline text-xl font-extrabold text-on-surface"
                  >
                    <Bilingual {...copy.dashboard.needsAction} />
                  </h2>
                </div>
              </div>
              <Link
                to="/registrations"
                className="inline-flex items-center gap-1 text-xs font-label font-bold text-primary hover:text-primary-container"
              >
                <Bilingual {...copy.dashboard.viewAllTasks} />
                <ChevronRight size={14} />
              </Link>
            </div>
            <div className="mt-5 space-y-3">
              {isTasksLoading ? (
                <div className="flex min-h-32 items-center justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : pendingTasks && pendingTasks.length > 0 ? (
                pendingTasks.map(task => {
                  const Icon = task.icon;
                  return (
                    <Link
                      key={task.id}
                      to={task.to}
                      className="group flex min-w-0 items-center gap-4 rounded-2xl bg-surface-container-lowest p-4 transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
                    >
                      <div
                        className={`grid size-10 shrink-0 place-items-center rounded-xl ${task.bgColor} ${task.color}`}
                      >
                        <Icon size={19} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="m-0 truncate text-sm font-body font-bold text-on-surface">
                          {task.title}
                        </p>
                        <p className="mt-1 truncate text-xs text-on-surface-variant">
                          {task.program}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-label font-bold text-primary">
                        <span className="hidden sm:inline">{task.btnLabel}</span>
                        <ChevronRight
                          size={16}
                          className="transition-transform group-hover:translate-x-0.5"
                        />
                      </span>
                    </Link>
                  );
                })
              ) : (
                <div className="rounded-2xl bg-surface-container-lowest px-5 py-10 text-center">
                  <LayoutDashboard size={28} className="mx-auto text-primary/40" />
                  <p className="mt-3 text-sm font-body text-on-surface">
                    <Bilingual {...copy.dashboard.allCaughtUp} />
                  </p>
                  <Link
                    to="/reports"
                    className="mt-3 inline-flex text-xs font-label font-bold text-primary hover:underline"
                  >
                    <Bilingual {...copy.dashboard.reportingCenter} />
                  </Link>
                </div>
              )}
            </div>
          </section>

          <section aria-labelledby="announcements-title">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-primary">
                  <Bilingual {...copy.dashboard.announcementHub} />
                </p>
                <h2
                  id="announcements-title"
                  className="mt-1 font-headline text-xl font-extrabold text-on-surface"
                >
                  <Bilingual {...copy.announcements.latestAnnouncements} />
                </h2>
              </div>
              <Link
                to="/announcements"
                className="inline-flex items-center gap-1 text-xs font-label font-bold text-primary hover:text-primary-container"
              >
                <Bilingual {...copy.announcements.viewAll} />
                <ChevronRight size={14} />
              </Link>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {activeAnnouncements && activeAnnouncements.length > 0 ? (
                activeAnnouncements.slice(0, 3).map(ann => (
                  <button
                    key={ann.id}
                    type="button"
                    onClick={() => setSelectedAnnouncement(ann)}
                    className="min-w-0 rounded-2xl bg-surface-container-lowest p-4 text-left shadow-level1 transition-colors hover:bg-surface-container-low focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-[0.99]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      {ann.priority === 'URGENT' ? (
                        <AlertCircle size={16} className="shrink-0 text-error" />
                      ) : (
                        <Megaphone size={16} className="shrink-0 text-primary" />
                      )}
                      <span className="text-[10px] text-on-surface-variant">
                        {ann.createdAt
                          ? new Date(ann.createdAt).toLocaleDateString(
                              language === 'ar' ? 'ar-EG' : 'id-ID',
                              { month: 'short', day: 'numeric' }
                            )
                          : '-'}
                      </span>
                    </div>
                    <h3 className="mt-4 line-clamp-2 font-headline text-sm font-bold text-on-surface">
                      {ann.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-on-surface-variant">
                      {ann.content}
                    </p>
                  </button>
                ))
              ) : (
                <div className="rounded-2xl bg-surface-container-low p-6 text-center text-xs text-on-surface-variant md:col-span-3">
                  <Bilingual {...copy.announcements.noNotifications} />
                </div>
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section
            aria-labelledby="programs-title"
            className="rounded-3xl bg-surface-container-low p-5 sm:p-6"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-primary">
                  <Bilingual {...copy.dashboard.activeProgramPortfolio} />
                </p>
                <h2
                  id="programs-title"
                  className="mt-1 font-headline text-xl font-extrabold text-on-surface"
                >
                  <Bilingual {...copy.dashboard.activeProgramsCard} />
                </h2>
              </div>
              <Link
                to="/programs"
                className="grid size-9 place-items-center rounded-lg bg-surface-container-lowest text-primary transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                aria-label={t(copy.dashboard.viewAllPrograms)}
              >
                <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="mt-5 space-y-3">
              {chartData.length > 0 ? (
                chartData.slice(0, 5).map(program => {
                  const coverUrl = resolveMediaUrl(program.coverImage, apiBaseUrl);
                  const programCompletion = Math.min(Math.max(program.completion, 0), 100);
                  return (
                    <Link
                      key={program.id || program.name}
                      to={program.id ? `/programs/${program.id}` : '/programs'}
                      className="block rounded-2xl bg-surface-container-lowest p-3.5 transition-colors hover:bg-surface-container-high focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                    >
                      <div className="flex items-center gap-3">
                        {coverUrl ? (
                          <img
                            src={coverUrl}
                            alt={program.name}
                            className="size-11 shrink-0 rounded-xl object-cover"
                          />
                        ) : (
                          <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                            <BookOpen size={17} />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="line-clamp-2 text-xs font-body font-bold text-on-surface">
                              {program.fullName || program.name}
                            </h3>
                            <span className="text-xs font-mono font-bold text-primary">
                              {programCompletion}%
                            </span>
                          </div>
                          <p className="mt-1 truncate text-[10px] text-on-surface-variant">
                            {t(copy.dashboard.participantsCount).replace(
                              '{count}',
                              String(program.participants)
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-container">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${programCompletion}%` }}
                        />
                      </div>
                    </Link>
                  );
                })
              ) : (
                <div className="rounded-2xl bg-surface-container-lowest px-4 py-8 text-center text-xs text-on-surface-variant">
                  <Bilingual {...copy.dashboard.noActivePrograms} />
                </div>
              )}
            </div>
          </section>

          <section className="rounded-3xl bg-surface-container-high p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-primary">
                  <Bilingual {...copy.dashboard.systemStatus} />
                </p>
                <h2 className="mt-1 font-headline text-xl font-extrabold text-on-surface">
                  {systemStatus}
                </h2>
              </div>
              <Cloud size={20} className="text-primary" />
            </div>
            <div className="mt-5 grid gap-3">
              <Link
                to="/settings"
                className="flex items-center gap-3 rounded-2xl bg-surface-container-lowest p-3.5 transition-colors hover:bg-surface-container-low focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              >
                <span
                  className={`size-2.5 shrink-0 rounded-full ${mailHealth?.status === 'CONNECTED' ? 'bg-primary' : 'bg-error'}`}
                />
                <span className="min-w-0 flex-1">
                  <strong className="block text-xs text-on-surface">
                    <Bilingual {...copy.dashboard.smtpStatus} />
                  </strong>
                  <span className="mt-1 block text-[10px] text-on-surface-variant">
                    {mailHealth?.status === 'CONNECTED'
                      ? t(copy.dashboard.smtpActive)
                      : systemStatus}
                  </span>
                </span>
                <ChevronRight size={16} className="text-primary" />
              </Link>
              <Link
                to="/settings"
                className="flex items-center gap-3 rounded-2xl bg-surface-container-lowest p-3.5 transition-colors hover:bg-surface-container-low focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              >
                <span className="size-2.5 shrink-0 rounded-full bg-primary" />
                <span className="min-w-0 flex-1">
                  <strong className="block text-xs text-on-surface">
                    <Bilingual {...copy.dashboard.storageStatus} />
                  </strong>
                  <span className="mt-1 block text-[10px] text-on-surface-variant">
                    <Bilingual {...copy.dashboard.storageS3} />
                  </span>
                </span>
                <ChevronRight size={16} className="text-primary" />
              </Link>
            </div>
            <Link
              to="/settings"
              className="mt-5 inline-flex items-center gap-1 text-xs font-label font-bold text-primary hover:text-primary-container"
            >
              <Bilingual {...copy.dashboard.details} />
              <ArrowUpRight size={14} />
            </Link>
          </section>

          <section className="rounded-3xl bg-primary-container p-6 text-on-primary-container">
            <ClipboardList size={20} className="text-secondary" />
            <h2 className="mt-4 font-headline text-lg font-extrabold">
              <Bilingual {...copy.dashboard.adminGuidance} />
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-on-primary-container/80">
              <Bilingual {...copy.dashboard.adminGuidanceDesc} />
            </p>
            <Link
              to="/settings"
              className="mt-5 inline-flex items-center gap-2 text-xs font-label font-bold text-secondary hover:text-on-primary"
            >
              <Sliders size={14} />
              <Bilingual {...copy.dashboard.emailSettings} />
              <ArrowUpRight size={14} />
            </Link>
          </section>
        </aside>
      </div>

      <AnnouncementDetailModal
        announcement={selectedAnnouncement}
        isOpen={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
      />
    </div>
  );
}
